import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { API } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { CheckCircle2, XCircle, Clock, MapPin, ExternalLink, ShieldCheck, Sprout } from "lucide-react";

function Big({ status }) {
  if (status === "verified") return (
    <div className="rounded-2xl bg-primary text-primary-foreground p-6 flex items-center gap-4" data-testid="verify-banner">
      <CheckCircle2 className="h-10 w-10 shrink-0"/>
      <div>
        <div className="text-xs uppercase tracking-[0.28em] text-white/80">Gudlink verified</div>
        <div className="font-marketing text-3xl">Trust-verified batch</div>
      </div>
    </div>
  );
  if (status === "rejected") return (
    <div className="rounded-2xl bg-destructive text-destructive-foreground p-6 flex items-center gap-4" data-testid="verify-banner">
      <XCircle className="h-10 w-10 shrink-0"/>
      <div>
        <div className="text-xs uppercase tracking-[0.28em] text-white/80">Not verified</div>
        <div className="font-marketing text-3xl">This batch failed review</div>
      </div>
    </div>
  );
  return (
    <div className="rounded-2xl bg-secondary text-secondary-foreground p-6 flex items-center gap-4" data-testid="verify-banner">
      <Clock className="h-10 w-10 shrink-0"/>
      <div>
        <div className="text-xs uppercase tracking-[0.28em] text-white/80">Under review</div>
        <div className="font-marketing text-3xl">Awaiting Gudlink approval</div>
      </div>
    </div>
  );
}

export default function VerifyBatch() {
  const { batchId } = useParams();
  const [batch, setBatch] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/batches/${batchId}`);
        setBatch(data);
        // record scan (fire and forget)
        api.post(`/verify/${batchId}/scan`).catch(()=>{});
      } catch (e) { setErr("Batch not found"); }
    })();
  }, [batchId]);

  if (err) return (
    <div className="max-w-2xl mx-auto px-6 py-16 text-center">
      <div className="rounded-2xl border border-destructive/40 bg-destructive/5 p-10">
        <XCircle className="h-10 w-10 mx-auto text-destructive"/>
        <h1 className="font-marketing text-3xl mt-3">This batch could not be found</h1>
        <p className="text-muted-foreground mt-2">The QR you scanned may be invalid or belong to another system.</p>
        <Button asChild className="mt-6 bg-primary hover:bg-primary/90"><Link to="/">Back to Gudlink</Link></Button>
      </div>
    </div>
  );

  if (!batch) return <div className="max-w-3xl mx-auto px-6 py-16 text-muted-foreground">Verifying…</div>;

  const checklistDone = (batch.sop_checklist || []).filter(c => c.checked).length;
  const checklistTotal = (batch.sop_checklist || []).length;

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-10">
      <Big status={batch.status}/>

      <div className="mt-6 rounded-2xl bg-card border border-border p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-secondary">{batch.product_type}</div>
            <h1 className="font-marketing text-4xl mt-1">{batch.batch_code}</h1>
            <div className="mt-1 text-muted-foreground text-sm flex items-center gap-1"><MapPin className="h-4 w-4"/>{batch.source_location}</div>
          </div>
          <Badge variant="outline" className="capitalize">{batch.status}</Badge>
        </div>

        <div className="mt-6 grid sm:grid-cols-2 gap-4 text-sm">
          <div className="rounded-xl bg-muted/50 p-4">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1"><Sprout className="h-3.5 w-3.5"/>Producer</div>
            <div className="mt-2 font-heading font-semibold">{batch.fpo_org || batch.fpo_name}</div>
            <div className="text-muted-foreground">{batch.fpo_name}</div>
            <div className="text-muted-foreground">{batch.fpo_location}</div>
          </div>
          <div className="rounded-xl bg-muted/50 p-4">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Quantities &amp; dates</div>
            <div className="mt-2"><b>{batch.quantity_kg} kg</b> of {batch.product_type}</div>
            <div className="text-muted-foreground">Harvest: {batch.harvest_date}</div>
            <div className="text-muted-foreground">Processing: {batch.processing_date}</div>
          </div>
        </div>

        {batch.notes && (
          <div className="mt-4 text-sm text-muted-foreground">
            <span className="uppercase tracking-[0.2em] text-xs mr-2">Notes</span>{batch.notes}
          </div>
        )}

        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          {[
            { k: "lab_report", label: "3rd-party Lab report" },
            { k: "sop_report", label: "SOP / Hygiene report" },
          ].map(item => {
            const f = batch[item.k];
            return (
              <div key={item.k} className="rounded-xl border border-border p-4">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{item.label}</div>
                {f ? (
                  <a
                    href={`${API}/files?path=${encodeURIComponent(f.storage_path)}`}
                    target="_blank" rel="noreferrer"
                    className="mt-2 inline-flex items-center text-primary text-sm hover:underline"
                    data-testid={`public-view-${item.k}`}
                  >
                    View document <ExternalLink className="h-3.5 w-3.5 ml-1"/>
                  </a>
                ) : (
                  <div className="mt-2 text-sm text-muted-foreground">Not provided</div>
                )}
              </div>
            );
          })}
        </div>

        {checklistTotal > 0 && (
          <div className="mt-6 rounded-xl border border-border p-4">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5"/>SOP checklist</div>
              <div className="text-xs text-muted-foreground">{checklistDone}/{checklistTotal} confirmed</div>
            </div>
            <ul className="mt-2 grid sm:grid-cols-2 gap-2 text-sm">
              {batch.sop_checklist.map((c, i) => (
                <li key={i} className="flex items-center gap-2">
                  {c.checked ? <CheckCircle2 className="h-4 w-4 text-primary shrink-0"/> : <XCircle className="h-4 w-4 text-muted-foreground shrink-0"/>}
                  <span className={c.checked ? "" : "text-muted-foreground"}>{c.label}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="mt-6 text-xs text-center text-muted-foreground">
        This is a public verification page powered by Gudlink. Records are recorded from the FPO/processor and are not editable by buyers.
      </div>
    </div>
  );
}
